import {VideoGenerationConfig} from '../models/AI/VideoGenerationConfig';
import {ApiResponse, PaginatedApiReponse} from '../models/ApiResponse';
import {GeneratedVideoChunk} from '../models/LibraryFile/GeneratedVideoChunk';
import {
  FileOrientation,
  MediaFile,
  VideoTranscriptSegment,
  VimeoMediaFile,
} from '../models/LibraryFile/MediaFile';
import {UploadUrlResponse} from '../models/LibraryFile/UploadUrlResponse';
import {StoryblocksMediaResponse} from '../models/Storyblocks/StoryblocksMediaResponse';
import {apiClient, withAuth} from './baseApiClient';
import {EditChunkOption} from '../repositories/LibraryRepository';

class LibraryApi {
  public async uploadLibraryVideo(
    organizationId: string,
    video: File,
    duration: number,
  ): Promise<ApiResponse<any>> {
    const headers = await withAuth({});
    const form = new FormData();
    form.append('file', video);
    form.append('duration', duration.toString());
    return apiClient.post(`/mediaLibrary/${organizationId}/upload`, form, {
      headers,
    });
  }

  public uploadFileToStorage = async (file: File, signedUrl: string) => {
    return fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/octet-stream',
      },
      body: file,
    });
  };

  public async generateVideoUploadLink(
    organizationId: string,
    type: string,
    fileSize: number,
  ): Promise<ApiResponse<UploadUrlResponse>> {
    const headers = await withAuth({});
    return apiClient.post(
      `/mediaLibrary/${organizationId}/upload_url?type=${type}&fileSizeInMb=${fileSize}`,
      {},
      {headers},
    );
  }

  public async createMediaLibraryFile(
    organizationId: string,
    storageFileName: string,
    type: string,
    name: string,
    fileSizeInMb: number,
    duration?: number,
    width?: number,
    height?: number,
    targetRes?: number | null,
  ): Promise<ApiResponse<UploadUrlResponse>> {
    const headers = await withAuth({});
    return apiClient.post(
      `/mediaLibrary/${organizationId}/add`,
      {
        storageFileName,
        type,
        name,
        fileSizeInMb,
        duration,
        width,
        height,
        videoResolution: targetRes,
      },
      {headers},
    );
  }

  public async getLibraryFiles(
    page: number,
    organizationId: string,
    pageSize: number,
    orderBy: string,
    typeFilter: number,
    search?: string,
    orderAsk?: boolean,
    fileStatusFilter?: number,
  ): Promise<PaginatedApiReponse<MediaFile>> {
    const headers = await withAuth({});
    return apiClient.post(
      `/mediaLibrary/${organizationId}/list`,
      {
        page,
        pageSize,
        orderBy,
        typeFilter,
        search,
        orderAsk,
        fileStatusFilter,
      },
      {headers},
    );
  }

  public async getFileByContentId(
    organizationId: string,
    contnentId: string,
  ): Promise<ApiResponse<MediaFile>> {
    const headers = await withAuth({});
    return apiClient.get(
      `/mediaLibrary/${organizationId}/get_by_content/${contnentId}`,
      {headers},
    );
  }

  public async downloadTranscript(
    organizationId: string,
    mediaFileId: string,
  ): Promise<ApiResponse<VideoTranscriptSegment[]>> {
    return apiClient.get(
      `/mediaLibrary/${organizationId}/transcript/${mediaFileId}`,
    );
  }

  public async deleteMediaFile(
    organizationId: string,
    mediaFileId: string,
  ): Promise<ApiResponse<any>> {
    const headers = await withAuth({});
    return apiClient.delete(`/mediaLibrary/${organizationId}/${mediaFileId}`, {
      headers,
    });
  }

  public async getVimeoVideos(
    organizationId: string,
    page: number,
    pageSize: number,
  ): Promise<ApiResponse<VimeoMediaFile[]>> {
    const headers = await withAuth({});
    return apiClient.get(
      `/mediaLibrary/${organizationId}/vimeo/videos?page=${page}&pageSize=${pageSize}`,
      {headers},
    );
  }

  public async generateAiVideosFromSlides(
    organizationId: string,
    config: VideoGenerationConfig,
    assistantAvatar?: File,
  ): Promise<ApiResponse<MediaFile[]>> {
    const result: ApiResponse<{assistantId?: string}> = await apiClient.post(
      `/mediaLibrary/${organizationId}/generate_video_from_slide_new`,
      config,
    );
    if (result.success && assistantAvatar && result.data?.assistantId) {
      await this.uploadAssistantAvatar(
        organizationId,
        result.data.assistantId,
        assistantAvatar,
      );
    }
    return result as unknown as ApiResponse<MediaFile[]>;
  }

  private async uploadAssistantAvatar(
    organizationId: string,
    assistantId: string,
    avatar: File,
  ): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('avatar', avatar);
    form.append('assistantId', assistantId);
    return apiClient.post(
      `/videoQuestions/${organizationId}/ai_assistant/upload_avatar`,
      form,
    );
  }

  public async getVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
  ): Promise<ApiResponse<GeneratedVideoChunk[]>> {
    return apiClient.get(
      `/mediaLibrary/${organizationId}/generate_video_from_slide/${mediaFileId}/chunks`,
    );
  }

  public async updateVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
    chunks: GeneratedVideoChunk[],
  ): Promise<ApiResponse<GeneratedVideoChunk[]>> {
    return apiClient.post(
      `/mediaLibrary/${organizationId}/generate_video_from_slide/${mediaFileId}/chunks`,
      chunks,
    );
  }

  public async compileVideoFile(
    organizationId: string,
    mediaFileId: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/mediaLibrary/${organizationId}/generate_video_from_slide/${mediaFileId}/compile`,
    );
  }

  public async getFileOrientation(
    organizationId: string,
    fileId: string,
  ): Promise<ApiResponse<FileOrientation>> {
    return apiClient.get(
      `/mediaLibrary/${organizationId}/get_file_orientation?mediaFileId=${fileId}`,
    );
  }

  public async editChunk(
    organizationId: string,
    mediaFileId: string,
    option: number,
    chunkId: string,
    additionalParameters: Record<string, any>,
  ): Promise<ApiResponse<FileOrientation>> {
    return apiClient.post(
      `/MediaLibrary/${organizationId}/edit/${mediaFileId}`,
      {option: option, aiVideoChunkId: chunkId, ...additionalParameters},
    );
  }

  public async saveEdit(
    organizationId: string,
    mediaFileId: string,
  ): Promise<ApiResponse<FileOrientation>> {
    return apiClient.post(
      `/MediaLibrary/${organizationId}/edit/${mediaFileId}/save`,
      {},
    );
  }

  public async cancelEdit(
    organizationId: string,
    mediaFileId: string,
  ): Promise<ApiResponse<FileOrientation>> {
    return apiClient.post(
      `/MediaLibrary/${organizationId}/edit/${mediaFileId}/cancel`,
      {},
    );
  }

  public async getStoryblocksMedia(
    organizationId: string,
    chunkId: string,
    page: number,
    pageSize: number,
    search: string,
  ): Promise<ApiResponse<StoryblocksMediaResponse>> {
    return apiClient.post(`/MediaLibrary/${organizationId}/edit/storyblocks`, {
      chunkId: chunkId,
      page: page,
      pageSize: pageSize,
      search: search,
    });
  }

  public async replaceChunkWithLibraryMedia(
    organizationId: string,
    mediaFileId: string,
    aiVideoChunkId: string,
    libraryMediaId: string,
    isImage: boolean,
  ): Promise<ApiResponse<any>> {
    const headers = await withAuth({
      'Content-Type': 'application/json-patch+json',
    });
    const body = {
      AiVideoChunkId: aiVideoChunkId,
      Option: isImage ? 6 : 5,
      LibraryMediaId: libraryMediaId,
    };
    return apiClient.post(
      `/MediaLibrary/${organizationId}/edit/${mediaFileId}`,
      body,
      {headers},
    );
  }

  public async getChunkNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
  ): Promise<ApiResponse<string>> {
    return apiClient.get(
      `/MediaLibrary/${organizationId}/edit/${mediaFileId}/${chunkId}/narration`,
    );
  }

  public async updateChunkNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    text: string,
  ): Promise<ApiResponse<any>> {
    return apiClient.post(
      `/MediaLibrary/${organizationId}/edit/${mediaFileId}`,
      {
        Option: EditChunkOption.UpdateNarration,
        AiVideoChunkId: chunkId,
        Text: text,
      },
    );
  }
}

export default LibraryApi;
